import { useEffect, useRef, useState } from 'react';

import { Group, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useRenameCodename } from '@/hooks/useRenameCodename';
import classes from './EditableCodename.module.css';

/** Free-text rename rules mirrored from the backend validator. */
const VALID_CODENAME_RE = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;

export interface EditableCodenameProps {
  /** The family codename displayed (and edited) in the input. */
  value: string;
  /**
   * PATCH key when it differs from `value`. Defaults to `value` — for
   * enquiry/self-served-report roots they're the same string.
   */
  familyKey?: string;
  /**
   * Non-editable rendered suffix (e.g. `-1` for a report request),
   * displayed dimmed after the input. The rename applies to the family
   * portion only.
   */
  suffix?: string | null;
  size?: 'xs' | 'sm' | 'md';
  fw?: React.CSSProperties['fontWeight'];
  disabled?: boolean;
  /** Called after a successful rename with the NEW family key. */
  onSaved?: (newFamilyKey: string) => void | Promise<void>;
  /**
   * Called on a 404 (the family was renamed under this view). Callers
   * should silently refetch — no toast is shown for this case.
   */
  onStaleKey?: () => void | Promise<void>;
  'data-testid'?: string;
}

/**
 * Click-to-edit codename with deliberately low visual affordance: reads
 * as plain text until hover (hairline border) / focus (highlight).
 * Enter or blur commits; Escape reverts; empty, unchanged, or invalid
 * input reverts without a request.
 */
export function EditableCodename({
  value,
  familyKey,
  suffix,
  size = 'sm',
  fw = 600,
  disabled = false,
  onSaved,
  onStaleKey,
  'data-testid': dataTestId,
}: Readonly<EditableCodenameProps>) {
  const [draft, setDraft] = useState(value);
  const escaping = useRef(false);
  const { rename, isSaving } = useRenameCodename();

  // Re-sync when the server value changes underneath (poll refresh,
  // parent refetch after our own save). Depends on `value` only — an
  // in-flight save must not clobber the analyst's draft.
  useEffect(() => {
    if (!isSaving) {
      setDraft(value);
    }
  }, [value]);

  const commit = async () => {
    if (escaping.current) {
      escaping.current = false;
      setDraft(value);
      return;
    }
    const next = draft.trim();
    if (!next || next === value) {
      setDraft(value);
      return;
    }
    if (!VALID_CODENAME_RE.test(next) || next.length < 2 || next.length > 64) {
      setDraft(value);
      notifications.show({
        title: 'Invalid codename',
        message:
          'Codenames are 2-64 characters of letters, digits and hyphens (no leading/trailing hyphen).',
        color: 'red',
      });
      return;
    }
    const result = await rename(familyKey ?? value, next);
    if (result.ok) {
      await onSaved?.(result.familyKey);
      return;
    }
    setDraft(value);
    if (result.reason === 'stale') {
      await onStaleKey?.();
      return;
    }
    notifications.show({
      title: 'Rename failed',
      message: result.message,
      color: 'red',
    });
  };

  return (
    <Group gap={2} wrap="nowrap" style={{ minWidth: 0 }}>
      <TextInput
        variant="unstyled"
        size={size}
        value={draft}
        disabled={disabled || isSaving}
        classNames={{ input: classes.input }}
        styles={{
          input: {
            fontWeight: fw,
            // Grow with the content so the field reads as inline text.
            width: `${Math.max(draft.length, 4) + 2}ch`,
          },
        }}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
          if (e.key === 'Escape') {
            escaping.current = true;
            e.currentTarget.blur();
          }
        }}
        // The codename often sits inside clickable rows/headers — an
        // edit click must never trigger row navigation or collapse.
        onClick={(e) => e.stopPropagation()}
        aria-label="Rename codename"
        data-testid={dataTestId ?? 'editable-codename'}
      />
      {suffix && (
        <Text size={size} c="dimmed" fw={fw}>
          {suffix}
        </Text>
      )}
    </Group>
  );
}
